import { BookingCard } from "@/components/booking/BookingCard";
import { EmptyState } from "@/components/common/EmptyState";
import { ScreenHeader } from "@/components/common/ScreenHeader";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useBookings } from "@/hooks/queries/use-bookings-query";
import { queryKeys } from "@/lib/query-keys";
import { isCustomerRole } from "@/lib/roles";
import { useQueryClient } from "@tanstack/react-query";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Booking } from "../../lib/services/bookings";

const bookingFilterOptions = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "cancelled" },
];

export default function BookingsScreen() {
  const { status } = useLocalSearchParams();
  const router = useRouter();
  const { theme, user } = useAuth();
  const [filter, setFilter] = useState<string>("all");
  const qc = useQueryClient();

  const params =
    filter !== "all" ? { status: filter } : { status: status as string };
  const { data, isLoading, isRefetching, refetch } = useBookings(params);
  const bookings: Booking[] = data?.data ?? [];

  const onRefresh = () => {
    // Force-invalidate so the next refetch always goes to the network
    qc.invalidateQueries({ queryKey: queryKeys.bookings.list(params) });
    refetch();
  };

  const navigateToBooking = useCallback(
    (item: Booking) => {
      if (item.status === "pending" || item.status === "quoted") {
        router.push(`/bookings/${item.id}/search-driver`);
      } else {
        router.push(`/bookings/${item.id}`);
      }
    },
    [router],
  );

  const renderBookingCard = useCallback(
    ({ item }: { item: Booking }) => (
      <BookingCard
        booking={item}
        backgroundColor={theme.card}
        borderColor={theme.border}
        onPress={() => navigateToBooking(item)}
        onConfirm={() => navigateToBooking(item)}
      />
    ),
    [navigateToBooking, theme.border, theme.card],
  );

  const keyExtractor = useCallback((item: Booking) => item.id.toString(), []);

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader title="My Bookings" />
      <View
        style={[styles.filterContainer, { borderBottomColor: theme.border }]}
      >
        {bookingFilterOptions.map((option) => {
          const isActive = filter === option.value;
          return (
            <TouchableOpacity
              key={option.value}
              style={styles.filterTab}
              onPress={() => setFilter(option.value)}
              activeOpacity={0.7}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={`${option.label} bookings`}
            >
              <ThemedText
                style={[styles.filterText, isActive && styles.filterTextActive]}
                type="smallSemiBold"
              >
                {option.label}
              </ThemedText>
              {isActive ? <View style={styles.filterIndicator} /> : null}
            </TouchableOpacity>
          );
        })}
      </View>

      {isLoading ? (
        <Loader inline />
      ) : (
        <FlatList
          data={bookings}
          renderItem={renderBookingCard}
          keyExtractor={keyExtractor}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={onRefresh}
              tintColor={BrandColors.primary}
            />
          }
          ListEmptyComponent={
            <EmptyState
              icon="calendar-outline"
              title="No bookings found"
              subtitle={
                filter === "all"
                  ? "You haven't made any bookings yet."
                  : `No ${bookingFilterOptions.find((o) => o.value === filter)?.label.toLowerCase() ?? filter.replace("_", " ")} bookings found.`
              }
              actionLabel={filter === "all" ? "Create booking" : undefined}
              onAction={
                filter === "all"
                  ? isCustomerRole(user?.role)
                    ? () => router.push("/(tabs)")
                    : undefined
                  : undefined
              }
            />
          }
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BrandColors.backgroundGray,
  },
  filterContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    marginBottom: 4,
  },
  filterTab: {
    flex: 1,
    alignItems: "center",
    paddingTop: 12,
    paddingBottom: 10,
    position: "relative",
  },
  filterText: {
    color: BrandColors.mediumGray,
    fontSize: 14,
  },
  filterTextActive: {
    color: BrandColors.primary,
  },
  filterIndicator: {
    position: "absolute",
    bottom: 0,
    left: 8,
    right: 8,
    height: 3,
    borderRadius: 2,
    backgroundColor: BrandColors.primary,
  },
  listContent: {
    padding: 16,
    paddingTop: 12,
  },
  emptyState: {
    alignItems: "center",
    paddingTop: 60,
  },
  emptyIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: BrandColors.primary + "10",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 16,
  },
  emptyText: {
    fontSize: 18,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: BrandColors.mediumGray,
    textAlign: "center",
    maxWidth: "70%",
    marginBottom: 24,
  },
});
